import { useQuery } from "@tanstack/react-query";
import { shouldShowQueryLoading } from "../../utils/queryHydration";
import Page404 from "../../components/Page404/Page404.tsx";
import { useEffect, useRef, useState } from "react";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

import "../../components/Esg/Esg.scss";
import {
  fetchGlobalData,
  getBlobAssetUrl,
  getStrapiMedia,
} from "../../utils/utils.tsx";
import { Esg_Plain } from "../../types/api/esg-international.ts";
import { BlocksRenderer } from "@strapi/blocks-react-renderer";
import FooterFormESG from "../../components/FooterFormESG/FooterFormESG.tsx";

const Esg = ({ locale }: { locale: string }) => {
  //const params = useParams();
  const numberRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Determine the correct API endpoint based on locale
  // For English (empty locale), use 'en' explicitly
  const effectiveLocale = locale || "en";
  const apiEndpoint = `/api/esg-international?customPopulate=nested&locale=${effectiveLocale}`;

  const {
    data: esg,
    error,
    isLoading,
    isError,
  } = useQuery<Esg_Plain>({
    queryKey: [apiEndpoint],
    queryFn: ({ queryKey }) => fetchGlobalData(queryKey[0] as string),
  });

  // Состояние для отслеживания открытых элементов в каждом списке аккордеона
  const [openedItems, setOpenedItems] = useState<{
    [key: string]: number | null;
  }>({
    "esg-accordion": null,
    "esg-faq__section-environmental": null,
    "esg-faq__section-social": null,
    "esg-faq__section-governance": null,
  });

  // Обработчик клика по элементу аккордеона
  const handleAccordionClick = (
    sectionId: string,
    index: number,
    hasContent: boolean,
  ) => {
    setOpenedItems((prev) => {
      const currentIndex = prev[sectionId];
      if (currentIndex === index) {
        return { ...prev, [sectionId]: null };
      } else {
        return { ...prev, [sectionId]: hasContent ? index : null };
      }
    });
  };

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.body.classList.add("esg-page-body");
    }
    return () => {
      if (typeof document !== "undefined") {
        document.body.classList.remove("esg-page-body");
      }
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const root = document.querySelector<HTMLElement>(".product-page-features");
    if (!root) return;

    const items = Array.from(
      root.querySelectorAll<HTMLDivElement>(".product-page-features__item"),
    );
    if (!items.length) return;

    // Initialize
    items.forEach((item) => {
      const numEl = item.querySelector<HTMLDivElement>(
        ".product-page-features__num",
      );
      const lineEl = item.querySelector<HTMLDivElement>(
        ".product-page-features__cont-line",
      );
      const title = item.querySelector<HTMLDivElement>(
        ".product-page-features__title",
      );
      const info = item.querySelector<HTMLDivElement>(
        ".product-page-features__info",
      );
      if (numEl) numEl.style.display = "none";
      if (lineEl) {
        lineEl.style.width = "0%";
      }
      if (title) title.style.opacity = "0.3";
      if (info) info.style.opacity = "0.3";
    });

    let ticking = false;
    let hasScrolled = false; // Flag to track if the user has scrolled

    // Detect initial scroll to avoid instant jump
    const handleInitialScroll = () => {
      hasScrolled = true;
      window.removeEventListener("scroll", handleInitialScroll);
    };
    window.addEventListener("scroll", handleInitialScroll, { passive: true });

    const compute = () => {
      ticking = false;
      const vh = window.innerHeight;
      const START = vh * 0.9;
      const END = vh * 0.65;

      // Find the index of the current element
      let currentIndex = -1;
      let currentProgress = 0;

      items.forEach((item, idx) => {
        const r = item.getBoundingClientRect();
        const raw = (START - r.top) / (START - END);
        const progress = Math.max(0, Math.min(1, raw));
        if (progress > 0 && progress < 1 && currentIndex === -1) {
          currentIndex = idx;
          currentProgress = progress;
        }
      });

      if (currentIndex === -1) {
        items.forEach((item, idx) => {
          const r = item.getBoundingClientRect();
          const raw = (START - r.top) / (START - END);
          if (raw >= 1) currentIndex = idx;
        });
        currentProgress = 1; // прогресс завершён
      }

      if (currentIndex === -1 && currentProgress === 0) {
        // If nothing is in progress, find the last fully completed element
        items.forEach((item, idx) => {
          const r = item.getBoundingClientRect();
          const raw = (START - r.top) / (START - END);
          if (raw >= 1) {
            currentIndex = idx;
          }
        });
      }

      items.forEach((item, idx) => {
        const numEl = item.querySelector<HTMLDivElement>(
          ".product-page-features__num",
        );
        const lineEl = item.querySelector<HTMLDivElement>(
          ".product-page-features__cont-line",
        );
        const title = item.querySelector<HTMLDivElement>(
          ".product-page-features__title",
        );
        const info = item.querySelector<HTMLDivElement>(
          ".product-page-features__info",
        );

        const effectiveProgress = hasScrolled ? currentProgress : 0;
        const isLastItemDone =
          idx === items.length - 1 && effectiveProgress >= 1;

        if (idx < currentIndex) {
          // Previous elements
          if (lineEl) lineEl.style.width = "0%";
          if (title) title.style.opacity = "1";
          if (info) info.style.opacity = "1";
          if (numEl) numEl.style.display = "none";
        } else if (idx === currentIndex) {
          // Current element
          if (isLastItemDone) {
            // Последний элемент завершил анимацию
            if (lineEl) lineEl.style.width = "0%"; // скрываем линию
            if (title) title.style.opacity = "1"; // текст полностью видим
            if (info) info.style.opacity = "1";
            if (numEl) numEl.style.display = "block";
          } else {
            // Анимация в процессе
            if (lineEl)
              lineEl.style.width = `${(effectiveProgress * 100).toFixed(3)}%`;
            const o = (0.3 + effectiveProgress * 0.7).toFixed(3);
            if (title) title.style.opacity = o;
            if (info) info.style.opacity = o;
            if (numEl)
              numEl.style.display =
                effectiveProgress > 0 && effectiveProgress < 1
                  ? "block"
                  : "none";
          }
        } else {
          // Future elements
          if (lineEl) lineEl.style.width = "0%";
          if (title) title.style.opacity = "0.3";
          if (info) info.style.opacity = "0.3";
          if (numEl) numEl.style.display = "none";
        }
      });
    };

    const onScroll = () => {
      const contRect = root.getBoundingClientRect();
      const outOfView =
        contRect.bottom < 0 || contRect.top > window.innerHeight * 1.2;
      if (outOfView) return;

      if (!ticking) {
        ticking = true;
        requestAnimationFrame(compute);
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    onScroll();

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      window.removeEventListener("scroll", handleInitialScroll);
    };
  }, [esg]);

  useEffect(() => {
    const animateNumber = (
      element: HTMLDivElement,
      endValue: number,
      duration: number,
      decimals: number = 0,
    ) => {
      let startTimestamp: number | null = null;
      const startValue = Math.max(0, endValue - (decimals > 0 ? 1 : 15));

      const step = (timestamp: number) => {
        if (!startTimestamp) startTimestamp = timestamp;
        const progress = Math.min((timestamp - startTimestamp) / duration, 1);
        const currentValue = startValue + (endValue - startValue) * progress;
        const unitMatch = element.getAttribute("data-unit") || "";
        element.textContent =
          decimals > 0
            ? currentValue.toFixed(decimals) + unitMatch
            : Math.floor(currentValue).toString() + unitMatch;

        if (progress < 1) {
          requestAnimationFrame(step);
        } else {
          const unitMatch = element.getAttribute("data-unit") || "";
          element.textContent =
            decimals > 0
              ? endValue.toFixed(decimals) + unitMatch
              : endValue.toString() + unitMatch;
        }
      };

      requestAnimationFrame(step);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const element = entry.target as HTMLDivElement;
            const rawValue = element.getAttribute("data-num") || "0";
            const normalizedValue = rawValue.replace(",", ".");
            const endValue = parseFloat(normalizedValue) || 0;
            const decimals = normalizedValue.includes(".")
              ? normalizedValue.split(".")[1]?.length || 0
              : 0;
            const startValue = Math.max(0, endValue - (decimals > 0 ? 1 : 15));
            const unitMatch = element.getAttribute("data-unit") || "";
            element.textContent =
              decimals > 0
                ? startValue.toFixed(decimals) + unitMatch
                : startValue.toString() + unitMatch;
            animateNumber(element, endValue, 2000, decimals);
          }
        });
      },
      { threshold: 0.5 },
    );

    numberRefs.current.forEach((ref) => {
      if (ref) {
        observer.observe(ref);
      }
    });

    // Store current ref values in local variable for cleanup
    const currentRefs = numberRefs.current;

    return () => {
      currentRefs.forEach((ref) => {
        if (ref) {
          observer.unobserve(ref);
        }
      });
    };
  }, [esg]);

  if (shouldShowQueryLoading(isLoading, esg))
    return <div className="loading-block" data-testid="loading-block"></div>;
  console.log(error);
  if (isError) return <div>Error: {error.message}</div>;
  if (!esg) return <div>Global error: fetch homepage</div>;
  if (!esg.data.Blocks.length) {
    return <Page404 locale={""} />;
  }
  console.log(esg.data);

  return (
    <>
      <div className="esg-page">
        {esg.data.contactTitle ? (
          <div className="btn-esg-contact">
            <a className="btn-border" href={esg.data.contactLink}>
              {esg.data.contactTitle}
            </a>
          </div>
        ) : (
          ""
        )}
        {/* eslint-disable @typescript-eslint/no-explicit-any -- Dynamic CMS component types */}
        {esg.data.Blocks?.map((block: any, index: number) => {
          switch (block.__component) {
            case "esg.start": {
              //console.log("FOUND", block);
              const hasContentStartEsg =
                block.title || block.description || block.buttons?.length;
              return (
                <section key={index} className={"esg-main " + block.classType}>
                  {hasContentStartEsg && (
                    <div className="wrapper">
                      <div className="esg-main__cont">
                        {block.title && <h1>{block.title}</h1>}
                        {block.description && <p>{block.description}</p>}
                        {block.buttons?.map((btn: any, index: number) => (
                          <div className="esg-main__btn">
                            <a
                              key={index}
                              className={
                                index === 0 ? "btn-black" : "btn-border"
                              }
                              href={btn.link}
                            >
                              {btn.title}
                            </a>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="esg-main__img">
                    {block.image ? (
                      <img src={getStrapiMedia(block.image.url)} alt="" />
                    ) : (
                      ""
                    )}
                  </div>
                </section>
              );
            }

            case "esg.accordion":
              //console.log("FOUND", block);
              return (
                <div
                  className={"esg-accordion accordion-inf " + block.classType}
                >
                  <div className="wrapper">
                    <div className="esg-accordion__left">
                      {block.image ? (
                        <img src={getStrapiMedia(block.image.url)} alt="" />
                      ) : (
                        ""
                      )}
                    </div>
                    <div className="esg-accordion__right">
                      <div className="accordion-inf-list">
                        {block.items?.map((item: any, index: number) => (
                          <div
                            key={index}
                            className={`accordion-inf-list__item ${
                              openedItems["esg-accordion"] === index
                                ? "opened"
                                : ""
                            }`}
                            onClick={() =>
                              handleAccordionClick("esg-accordion", index, true)
                            }
                          >
                            <div className="accordion-inf-list__title">
                              {item.title}
                              <div className="show-full">
                                {openedItems["esg-accordion"] === index
                                  ? "Hide"
                                  : "More"}
                              </div>
                            </div>
                            <div className="accordion-inf-list__cont">
                              {item.textFormat ? (
                                <BlocksRenderer content={item.textFormat} />
                              ) : (
                                <p>{item.text}</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="esg-accordion__right-inf">
                        <div className="esg-accordion__right-inf-img">
                          {block.image ? (
                            <img src={getStrapiMedia(block.image.url)} alt="" />
                          ) : (
                            ""
                          )}
                        </div>
                        <div className="esg-accordion__right-inf-text">
                          <p>{block.description}</p>
                          <div className="esg-accordion__right-btn">
                            {block.buttonText ? (
                              <a className="btn-black" href={block.buttonLink}>
                                {block.buttonText}
                              </a>
                            ) : (
                              ""
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );

            case "esg.goals":
              //console.log("FOUND", block);
              return (
                <div
                  key={index}
                  className="esg-block-inf"
                  style={{
                    backgroundImage: block.image?.url
                      ? `url(${getStrapiMedia(block.image.url)})`
                      : "none",
                  }}
                >
                  <div
                    className="esg-block-inf__img"
                    style={{
                      backgroundImage: block.image?.url
                        ? `url(${getStrapiMedia(block.image.url)})`
                        : "none",
                    }}
                  >
                    {block.image ? (
                      <img src={getStrapiMedia(block.image.url)} alt="" />
                    ) : (
                      ""
                    )}
                  </div>
                  <div className="wrapper">
                    <div className="esg-block-inf__title">
                      <div className="block-title">{block.title}</div>
                      <h2>{block.heading}</h2>
                      <p>{block.description}</p>
                    </div>
                    <div className="esg-block-inf__text">
                      <div className="esg-block-inf__text-title">
                        {block.goalsTitle ? (
                          <div className="block-title">{block.goalsTitle}</div>
                        ) : (
                          ""
                        )}
                      </div>
                      <div className="esg-block-inf__text-cont">
                        <ol>
                          {block.items?.map((goal: any, goalIndex: number) => (
                            <li key={goalIndex}>
                              {goal.text}
                              {goal.formatText ? (
                                <BlocksRenderer content={goal.formatText} />
                              ) : (
                                ""
                              )}
                            </li>
                          ))}
                        </ol>
                      </div>
                    </div>
                    {block.resultText && (
                      <div className="esg-block-inf__text">
                        <div className="esg-block-inf__text-title">
                          <div className="block-title">{block.resultTitle}</div>
                        </div>
                        <div className="esg-block-inf__text-cont">
                          <p>{block.resultText}</p>
                        </div>
                      </div>
                    )}
                    {block.resultAlterTitle ? (
                      <div className="esg-block-inf__text">
                        <div className="esg-block-inf__text-title">
                          {block.resultAlterTitle ? (
                            <div className="block-title">
                              {block.resultAlterTitle}
                            </div>
                          ) : (
                            ""
                          )}
                        </div>
                        <div className="esg-block-inf__text-cont">
                          <ol>
                            {block.resultAlterItems?.map(
                              (goal: any, goalIndex: number) => (
                                <li key={goalIndex}>
                                  {goal.text}
                                  {goal.formatText ? (
                                    <BlocksRenderer content={goal.formatText} />
                                  ) : (
                                    ""
                                  )}
                                </li>
                              ),
                            )}
                          </ol>
                        </div>
                      </div>
                    ) : (
                      ""
                    )}
                    <div
                      className="esg-block-inf__footer"
                      dangerouslySetInnerHTML={{ __html: block.footerText }}
                    />
                  </div>
                </div>
              );

            case "esg.faq-block":
              //console.log("FOUND", block);
              return (
                <div className="esg-faq">
                  <div className="wrapper">
                    <h2>{block.title}</h2>
                    {block.categories?.map(
                      (faqSection: any, faqIndex: number) => (
                        <div
                          key={faqIndex}
                          className="esg-faq__section accordion-inf"
                          id={`esg-faq__section-${faqSection.title.toLowerCase()}`}
                        >
                          <div className="block-title">{faqSection.title}</div>
                          <div className="accordion-inf-list">
                            {faqSection.items?.map(
                              (item: any, itemIndex: number) => (
                                <div
                                  key={itemIndex}
                                  className={`accordion-inf-list__item ${
                                    openedItems[
                                      `esg-faq__section-${faqSection.title.toLowerCase()}`
                                    ] === itemIndex
                                      ? "opened"
                                      : ""
                                  }`}
                                  onClick={() =>
                                    handleAccordionClick(
                                      `esg-faq__section-${faqSection.title.toLowerCase()}`,
                                      itemIndex,
                                      true,
                                    )
                                  }
                                >
                                  <div className="accordion-inf-list__title">
                                    {item.question}
                                    <div className="show-full">
                                      {openedItems[
                                        `esg-faq__section-${faqSection.title.toLowerCase()}`
                                      ] === itemIndex
                                        ? "Hide"
                                        : "More"}
                                    </div>
                                  </div>
                                  <div className="accordion-inf-list__cont">
                                    <BlocksRenderer content={item.answer} />
                                  </div>
                                </div>
                              ),
                            )}
                          </div>
                        </div>
                      ),
                    )}
                    <div className="esg-faq__footer">
                      <p>{block.footerText}</p>
                      <div className="esg-faq__footer-btn">
                        {block.buttonText ? (
                          <a className="btn-black" href={block.buttonLink}>
                            {block.buttonText}
                          </a>
                        ) : (
                          ""
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );

            case "esg.documents":
              //console.log("FOUND", block);
              return (
                <div>
                  <div className="esg-documents">
                    <div className="wrapper">
                      <h2>{block.title}</h2>
                      <div className="document-list">
                        {block.files?.map(
                          (documentItem: any, docIndex: number) => (
                            <div key={docIndex} className="document-list__item">
                              <div className="document-list__name">
                                {documentItem.name}
                              </div>
                              <div className="document-list__bottom">
                                <div className="document-list__bottom-left">
                                  {(documentItem.type || documentItem.size) && (
                                    <div className="document-list__file-info">
                                      {documentItem.type && (
                                        <span>{documentItem.type}</span>
                                      )}
                                      {documentItem.type &&
                                        documentItem.size &&
                                        ", "}
                                      {documentItem.size && (
                                        <span>{documentItem.size}</span>
                                      )}
                                    </div>
                                  )}
                                  {documentItem.updated && (
                                    <div className="document-list__updated">
                                      Updated:{" "}
                                      <span>{documentItem.updated}</span>
                                    </div>
                                  )}
                                </div>
                                <div className="document-list__bottom-right">
                                  {documentItem.file ? (
                                    <a
                                      href={getStrapiMedia(
                                        documentItem.file.url,
                                      )}
                                      target="_blank"
                                      className="document-icon-btn"
                                    >
                                      <img
                                        src="/img/view.svg"
                                        width="25"
                                        height="14"
                                        alt="View"
                                      />
                                    </a>
                                  ) : (
                                    ""
                                  )}
                                </div>
                              </div>
                            </div>
                          ),
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );

            default:
              return null;
          }
        })}
        {/* eslint-enable @typescript-eslint/no-explicit-any */}
      </div>

      {esg.data.contacts ? (
        <FooterFormESG data={esg.data.contacts} locale={""} />
      ) : (
        ""
      )}

      <div className="esg-page-footer">
        <div className="wrapper">
          <a href="/">
            <img
              width="104"
              height="13"
              src={getBlobAssetUrl("D_Trading_logo_2_plashka_aaed9da59d.svg")}
            />
          </a>
        </div>
      </div>
    </>
  );
};

export default Esg;
